import { useState, useEffect, useCallback, useRef } from "react";
import {
	getMediaFiles,
	uploadMediaFiles,
} from "../services/mediaLibraryClient";
import MediaUploadService from "../services/MediaUploadService";
import { DisplayMessage, Uuidv4 } from "../../../../services/helpers";
import { usePusher } from "../../../../hooks/usePusher";
import FileSaver from "file-saver";
import moment from "moment";

export const useMediaLibrary = (module, resources, dealerGroupId) => {
	const [mediaFiles, setMediaFiles] = useState([]);
	const [loading, setLoading] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [selectionMode, setSelectionMode] = useState(false);
	const [selectedFiles, setSelectedFiles] = useState([]);
	const [uploadService, setUploadService] = useState(null);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const processedFilesRef = useRef([]);

	useEffect(() => {
		setUploadService(new MediaUploadService(module, resources));
	}, [module, resources]);

	const fetchMediaFiles = useCallback(async () => {
		setLoading(true);
		try {
			const files = await getMediaFiles(module);
			setMediaFiles(files);
		} finally {
			setLoading(false);
		}
	}, [module]);

	useEffect(() => {
		fetchMediaFiles();
	}, [fetchMediaFiles]);

	const handleMediaProcessed = useCallback((data) => {
		if (processedFilesRef.current.includes(data.id)) {
			processedFilesRef.current = processedFilesRef.current.filter(
				(id) => id !== data.id
			);
		}

		// Merge the processed data with existing media files placeholders
		setMediaFiles((prevFiles) => {
			const updatedFiles = [...prevFiles];

			const index = updatedFiles.findIndex(
				(file) => file.originalContent === data.originalContent
			);

			if (index >= 0) {
				updatedFiles[index] = data;
			} else {
				updatedFiles.push(data);
			}

			return updatedFiles;
		});

		if (processedFilesRef.current.length === 0) {
			setUploading(false);
		}
	}, []);

	let channelName = null;

	switch(module) {
		case "messageTemplates": channelName = `messagetemplates-${dealerGroupId}`; break;
		case "signableDocuments": channelName = `signabledocuments-${dealerGroupId}`; break;
		default: channelName = null;
	}
	usePusher(channelName, "media-processed", handleMediaProcessed);

	const uploadFiles = async (files) => {
		setUploading(true);
		try {
			const result = await uploadMediaFiles(module, files);

			// Push placeholders for the files being uploaded
			const sasDataListPlaceholders = files.map((file) => ({
				id: Uuidv4(),
				extraSmallContent: file.blobUri,
				smallContent: file.blobUri,
				mediumContent: file.blobUri,
				originalContent: file.blobUri,
				isPlaceholder: true,
			}));
			setMediaFiles((prevFiles) => [...prevFiles, ...sasDataListPlaceholders]);

			if (result.success) {
				return true;
			} else if (result.error) {
				DisplayMessage(`${resources["UploadError"]}: ${result.error}`, "error");
				setUploading(false);
			}
			return result.success;
		} catch (error) {
			console.error("Error uploading files:", error);
			setUploading(false);
			return false;
		}
	};

	const copyFileUrl = (url) => {
		navigator.clipboard.writeText(url).then(
			() => {
				DisplayMessage(resources["URLCopiedToClipboard"], "success");
			},
			(err) => {
				console.error("Could not copy URL: ", err);
				DisplayMessage(resources["FailedToCopyURL"], "error");
			}
		);
	};

	const toggleSelectionMode = () => {
		setSelectionMode((prev) => !prev);
		setSelectedFiles([]);
	};

	const toggleFileSelection = (mediaFile) => {
		setSelectedFiles((prev) => {
			const fileId = mediaFile.id;

			const isCurrentlySelected = prev.includes(fileId);

			const newSelection = isCurrentlySelected
				? prev.filter((id) => id !== fileId)
				: [...prev, fileId];

			return newSelection;
		});
	};

	const selectAllFiles = () => {
		const allFileIds = mediaFiles.map((file) => file.id).filter(Boolean);
		setSelectedFiles(allFileIds);
	};

	const deselectAllFiles = () => {
		setSelectedFiles([]);
	};

	const openDeleteConfirmation = () => {
		if (selectedFiles.length === 0 || !uploadService) {
			return;
		}
		setDeleteModalOpen(true);
	};

	const closeDeleteConfirmation = () => {
		setDeleteModalOpen(false);
	};

	const deleteSelected = async () => {
		if (selectedFiles.length === 0 || !uploadService) {
			return;
		}

		try {
			const result = await uploadService.deleteMediaFiles(selectedFiles);

			if (result.success) {
				setMediaFiles((prevFiles) =>
					prevFiles.filter((file) => !selectedFiles.includes(file.id))
				);
				DisplayMessage(resources["MediaDeletedSuccessfully"], "success");
				setDeleteModalOpen(false);
				setSelectedFiles([]);
				setSelectionMode(false);
			} else {
				DisplayMessage(result.error, "error");
			}
		} catch (error) {
			console.error("Error deleting files:", error);
			DisplayMessage(resources["ErrorDeletingFiles"], "error");
		}
	};

	const downloadFiles = async (fileIds, sizeType = "original") => {
		const filesToDownload = mediaFiles.filter((file) =>
			fileIds.includes(file.id)
		);

		for (const file of filesToDownload) {
			const url =
				sizeType === "medium" ? file.mediumContent : file.originalContent;
			if (url) {
				const response = await fetch(url);
				const blob = await response.blob();

				const filename = getDownloadFilename(file);
				FileSaver.saveAs(blob, filename);
			}
		}

		DisplayMessage(
			`${filesToDownload.length} ${resources["FilesDownloaded"]}`,
			"success"
		);
	};

	const formatDownloadDateTime = (value) => {
		if (!value) return "";
		return moment(value).format("DD_MM_YYYY_HH:mm");
	};

	const getDownloadFilename = (file) => {
		const createdOnString = formatDownloadDateTime(file.createdOn);
		const description = file.description?.replaceAll("-", "_");
		return (
			[createdOnString, description].filter(Boolean).join("_") ||
			`media-${file.id}`
		);
	};

	return {
		mediaFiles,
		loading,
		uploading,
		fetchMediaFiles,
		uploadFiles,
		copyFileUrl,
		selectionMode,
		selectedFiles,
		toggleSelectionMode,
		toggleFileSelection,
		selectAllFiles,
		deselectAllFiles,
		deleteModalOpen,
		openDeleteConfirmation,
		closeDeleteConfirmation,
		deleteSelected,
		downloadFiles,
	};
};
